import { PIM_ACTION_TYPES } from './pim.type';
import { PIMAction, PIMReducerAction } from '../types/pim.action.interface';
import { IPIMState } from '../types/pim.interface';

const initialState: IPIMState = {
  pims: [],
  archivedPims: [],
  setupsByPimId: {},
  tabs: [],
  tabsRequestSetupId: null,
  subTabs: [],
  subTabsRequestSetupId: null,
  columns: [],
  columnsRequestSubTabId: null,
  requiredColumnsForTab: [],
  accordionSections: [],
  accordionSectionsRequestTabId: null,
  selectedPimId: null,
  selectedSetupId: null,
  selectedTabId: null,
  publishFilter: {
    channelSetupId: null,
    isPublished: null,
    page: 1,
    limit: 50,
  },
  productTable: {
    rows: [],
    total: 0,
    page: 1,
    limit: 15,
    isLoadingRows: false,
  },
  downloadLogs: {
    entries: [],
    total: 0,
    page: 1,
    limit: 50,
    isLoadingEntries: false,
  },
  columnMappingRegistry: [],
  allSetupColumns: [],
  metaLimits: {
    maxColumnConfigurations: 10,
  },
  channelSetupFilters: null,
  isLoading: false,
  isLoadingArchived: false,
  error: null,
};

/* eslint-disable default-param-last, sonarjs/cognitive-complexity, sonarjs/max-switch-cases */
const PIM_ACTION_TYPE_SET: ReadonlySet<string> = new Set(Object.values(PIM_ACTION_TYPES));

/** Type guard narrowing a reducer action to the precise {@link PIMAction} union. */
export const isPIMAction = (action: PIMReducerAction): action is PIMAction =>
  PIM_ACTION_TYPE_SET.has(action.type);

const PIMReducer = (state: IPIMState = initialState, action: PIMReducerAction): IPIMState => {
  if (!isPIMAction(action)) return state;
  switch (action.type) {
    case PIM_ACTION_TYPES.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case PIM_ACTION_TYPES.SET_LOADING_ARCHIVED:
      return { ...state, isLoadingArchived: action.payload };
    case PIM_ACTION_TYPES.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
    case PIM_ACTION_TYPES.SET_CHANNELS:
      if (action.meta?.archived) {
        return { ...state, archivedPims: action.payload, isLoadingArchived: false, isLoading: false };
      }
      return { ...state, pims: action.payload, isLoading: false };
    case PIM_ACTION_TYPES.SET_ARCHIVED_CHANNELS:
      return { ...state, archivedPims: action.payload };
    case PIM_ACTION_TYPES.UNARCHIVE_CHANNEL:
      return {
        ...state,
        archivedPims: state.archivedPims.filter((c) => c._id !== action.payload._id),
        pims: [action.payload, ...state.pims],
      };
    case PIM_ACTION_TYPES.ADD_CHANNEL:
      return { ...state, pims: [action.payload, ...state.pims] };
    case PIM_ACTION_TYPES.UPDATE_CHANNEL: {
      if (!action.payload?._id) return state;
      const { _id, isArchived } = action.payload;
      const activeWithout = state.pims.filter((c) => c._id !== _id);
      const archivedWithout = state.archivedPims.filter((c) => c._id !== _id);
      if (isArchived) {
        return {
          ...state,
          pims: activeWithout,
          archivedPims: [action.payload, ...archivedWithout],
        };
      }
      return {
        ...state,
        pims: state.pims.some((c) => c._id === _id)
          ? state.pims.map((c) => (c._id === _id ? action.payload : c))
          : [action.payload, ...activeWithout],
        archivedPims: archivedWithout,
      };
    }
    case PIM_ACTION_TYPES.REMOVE_CHANNEL:
      return {
        ...state,
        pims: state.pims.filter((c) => c._id !== action.payload),
        archivedPims: state.archivedPims.filter((c) => c._id !== action.payload),
      };
    case PIM_ACTION_TYPES.SET_SETUPS: {
      const { channelId, setups, isArchived } = action.payload;
      const previous = state.setupsByPimId[channelId] ?? [];
      const preserved = previous.filter((s) => !!s.isArchived !== !!isArchived);
      return {
        ...state,
        setupsByPimId: {
          ...state.setupsByPimId,
          [channelId]: [...preserved, ...setups],
        },
        isLoading: false,
      };
    }
    case PIM_ACTION_TYPES.ADD_SETUP: {
      const { channelId } = action.payload;
      const existing = state.setupsByPimId[channelId] ?? [];
      return {
        ...state,
        setupsByPimId: {
          ...state.setupsByPimId,
          [channelId]: [action.payload, ...existing],
        },
      };
    }
    case PIM_ACTION_TYPES.UPDATE_SETUP: {
      const cId = action.payload.channelId;
      return {
        ...state,
        setupsByPimId: {
          ...state.setupsByPimId,
          [cId]: (state.setupsByPimId[cId] ?? []).map((s) => (s._id === action.payload._id ? action.payload : s)),
        },
      };
    }
    case PIM_ACTION_TYPES.REMOVE_SETUP: {
      const { channelId, setupId } = action.payload;
      return {
        ...state,
        setupsByPimId: {
          ...state.setupsByPimId,
          [channelId]: (state.setupsByPimId[channelId] ?? []).filter((s) => s._id !== setupId),
        },
      };
    }
    case PIM_ACTION_TYPES.SET_TABS_REQUEST_SCOPE:
      return { ...state, tabs: [], tabsRequestSetupId: action.payload };
    case PIM_ACTION_TYPES.SET_TABS:
      if (action.channelSetupId !== undefined && action.channelSetupId !== state.tabsRequestSetupId) return state;
      return { ...state, tabs: action.payload, isLoading: false };
    case PIM_ACTION_TYPES.ADD_TAB:
      return { ...state, tabs: [...state.tabs, action.payload] };
    case PIM_ACTION_TYPES.UPDATE_TAB:
      return { ...state, tabs: state.tabs.map((t) => (t._id === action.payload._id ? action.payload : t)) };
    case PIM_ACTION_TYPES.REMOVE_TAB:
      return { ...state, tabs: state.tabs.filter((t) => t._id !== action.payload) };
    case PIM_ACTION_TYPES.SET_SUBTABS_REQUEST_SCOPE:
      return { ...state, subTabs: [], subTabsRequestSetupId: action.payload };
    case PIM_ACTION_TYPES.SET_SUBTABS:
      if (action.channelSetupId !== undefined && action.channelSetupId !== state.subTabsRequestSetupId) return state;
      return { ...state, subTabs: action.payload };
    case PIM_ACTION_TYPES.ADD_SUBTAB:
      return { ...state, subTabs: [...state.subTabs, action.payload] };
    case PIM_ACTION_TYPES.UPDATE_SUBTAB:
      return { ...state, subTabs: state.subTabs.map((s) => (s._id === action.payload._id ? action.payload : s)) };
    case PIM_ACTION_TYPES.REMOVE_SUBTAB:
      return { ...state, subTabs: state.subTabs.filter((s) => s._id !== action.payload) };
    case PIM_ACTION_TYPES.SET_ACCORDION_SECTIONS_REQUEST_SCOPE:
      return { ...state, accordionSections: [], accordionSectionsRequestTabId: action.payload };
    case PIM_ACTION_TYPES.SET_ACCORDION_SECTIONS:
      if (action.tabId !== undefined && action.tabId !== state.accordionSectionsRequestTabId) return state;
      return { ...state, accordionSections: action.payload };
    case PIM_ACTION_TYPES.ADD_ACCORDION_SECTION:
      return { ...state, accordionSections: [...state.accordionSections, action.payload] };
    case PIM_ACTION_TYPES.UPDATE_ACCORDION_SECTION:
      return {
        ...state,
        accordionSections: state.accordionSections.map((s) => (s._id === action.payload._id ? action.payload : s)),
      };
    case PIM_ACTION_TYPES.REMOVE_ACCORDION_SECTION:
      return { ...state, accordionSections: state.accordionSections.filter((s) => s._id !== action.payload) };
    case PIM_ACTION_TYPES.SET_COLUMNS_REQUEST_SCOPE:
      return { ...state, columns: [], columnsRequestSubTabId: action.payload };
    case PIM_ACTION_TYPES.SET_COLUMNS:
      if (action.subTabId !== undefined && action.subTabId !== state.columnsRequestSubTabId) return state;
      return { ...state, columns: action.payload };
    case PIM_ACTION_TYPES.SET_REQUIRED_COLUMNS_FOR_TAB:
      return { ...state, requiredColumnsForTab: action.payload };
    case PIM_ACTION_TYPES.SET_COLUMN_MAPPING_REGISTRY:
      return { ...state, columnMappingRegistry: action.payload };
    case PIM_ACTION_TYPES.SET_ALL_SETUP_COLUMNS:
      return { ...state, allSetupColumns: action.payload };
    case PIM_ACTION_TYPES.ADD_COLUMN:
      return { ...state, columns: [...state.columns, action.payload] };
    case PIM_ACTION_TYPES.UPDATE_COLUMN:
      return { ...state, columns: state.columns.map((c) => (c._id === action.payload._id ? action.payload : c)) };
    case PIM_ACTION_TYPES.REMOVE_COLUMN:
      return { ...state, columns: state.columns.filter((c) => c._id !== action.payload) };
    case PIM_ACTION_TYPES.SET_SELECTED_CHANNEL:
      return { ...state, selectedPimId: action.payload };
    case PIM_ACTION_TYPES.SET_SELECTED_SETUP:
      return { ...state, selectedSetupId: action.payload };
    case PIM_ACTION_TYPES.SET_SELECTED_TAB:
      return { ...state, selectedTabId: action.payload };
    case PIM_ACTION_TYPES.SET_PUBLISH_FILTER:
      return { ...state, publishFilter: action.payload };
    case PIM_ACTION_TYPES.SET_LOADING_ROWS:
      return { ...state, productTable: { ...state.productTable, isLoadingRows: action.payload } };
    case PIM_ACTION_TYPES.SET_PRODUCT_ROWS:
      return {
        ...state,
        productTable: {
          ...state.productTable,
          rows: action.payload.rows,
          total: action.payload.total,
          page: action.payload.page,
          limit: action.payload.limit,
          isLoadingRows: false,
        },
      };
    case PIM_ACTION_TYPES.UPDATE_ROW_CELL: {
      const { rowId, columnId, value, counts } = action.payload;
      return {
        ...state,
        productTable: {
          ...state.productTable,
          rows: state.productTable.rows.map((row) =>
            row._id === rowId
              ? {
                  ...row,
                  cellData: { ...row.cellData, [columnId]: { value, isManualOverride: true } },
                  filledRequiredCount: counts?.filledRequiredCount ?? row.filledRequiredCount,
                  totalRequiredCount: counts?.totalRequiredCount ?? row.totalRequiredCount,
                  needsRepublish: counts?.needsRepublish ?? row.needsRepublish,
                }
              : row,
          ),
        },
      };
    }
    case PIM_ACTION_TYPES.SET_LOADING_DOWNLOAD_LOGS:
      return {
        ...state,
        downloadLogs: { ...state.downloadLogs, isLoadingEntries: action.payload },
      };
    case PIM_ACTION_TYPES.SET_DOWNLOAD_LOGS:
      return {
        ...state,
        downloadLogs: {
          entries: action.payload.entries,
          total: action.payload.total,
          page: action.payload.page,
          limit: action.payload.limit,
          isLoadingEntries: false,
        },
      };
    case PIM_ACTION_TYPES.APPEND_DOWNLOAD_LOGS:
      return {
        ...state,
        downloadLogs: {
          ...state.downloadLogs,
          entries: [...state.downloadLogs.entries, ...action.payload.entries],
          total: action.payload.total,
          page: action.payload.page,
          limit: action.payload.limit,
          isLoadingEntries: false,
        },
      };
    case PIM_ACTION_TYPES.DELETE_DOWNLOAD_LOG: {
      const remainingEntries = state.downloadLogs.entries.filter((log) => log._id !== action.payload);
      const wasRemoved = remainingEntries.length !== state.downloadLogs.entries.length;
      return {
        ...state,
        downloadLogs: {
          ...state.downloadLogs,
          entries: remainingEntries,
          total: wasRemoved ? Math.max(0, state.downloadLogs.total - 1) : state.downloadLogs.total,
        },
      };
    }
    case PIM_ACTION_TYPES.SET_META_LIMITS:
      return { ...state, metaLimits: { ...state.metaLimits, ...action.payload } };
    case PIM_ACTION_TYPES.SET_CHANNEL_SETUP_FILTERS:
      return { ...state, channelSetupFilters: action.payload };
    case PIM_ACTION_TYPES.CLEAR_PIM:
      return initialState;
    default:
      return state;
  }
};

export default PIMReducer;
